import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ExportTranscript from '@Application/Beneficiaries/Transcripts/Queries/ExportTranscript';
import exportTranscript from '@Application/Beneficiaries/Transcripts/exportTranscript';
import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, Checkbox, Dialog, Message } from '@verclam/ui-kit';

const optionsStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const scopeWarningStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const footerStyle = css`
	display: flex;
	justify-content: flex-end;
	gap: 0.625rem;
`;

type Props = {
	readonly visible: boolean;
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly isReviewed: boolean;
	readonly hasSpeakers: boolean;
	readonly onHide: () => void;
};

/**
 * V-27. Both options default checked — the common case is a full export. `isReviewed` only decides
 * whether the non-blocking `Message` shows; the out-of-scope warning below it is unconditional, on
 * every export.
 */
const TranscriptExportDialog: React.FC<Props> = ({
	visible,
	facilityId,
	subject,
	interviewId,
	isReviewed,
	hasSpeakers,
	onHide,
}) => {
	const dispatch = useDispatch();
	const [includeSpeakers, setIncludeSpeakers] = React.useState(true);
	const [includeTimestamps, setIncludeTimestamps] = React.useState(true);
	const [isExporting, setIsExporting] = React.useState(false);

	const handleExport = React.useCallback(() => {
		setIsExporting(true);

		dispatch(
			exportTranscript(
				new ExportTranscript(facilityId, subject, interviewId, includeSpeakers, includeTimestamps),
			),
		)
			.then(() => onHide())
			.catch(ignoreHandledError) // toast shown by InterviewsNotifications on failure only
			.finally(() => setIsExporting(false));
	}, [dispatch, facilityId, subject, interviewId, includeSpeakers, includeTimestamps, onHide]);

	return (
		<Dialog
			visible={visible}
			onHide={onHide}
			header="Exporter le transcript"
			footer={
				<div css={footerStyle}>
					<Button type="button" label="Annuler" severity="secondary" outlined onClick={onHide} />
					<Button type="button" label="Exporter" loading={isExporting} onClick={handleExport} />
				</div>
			}
		>
			<div css={optionsStyle}>
				<ConditionalLoader data={hasSpeakers}>
					<Checkbox
						name="includeSpeakers"
						label="Inclure les locuteurs"
						checked={includeSpeakers}
						onChange={setIncludeSpeakers}
					/>
				</ConditionalLoader>
				<Checkbox
					name="includeTimestamps"
					label="Inclure les horodatages"
					checked={includeTimestamps}
					onChange={setIncludeTimestamps}
				/>
				{!isReviewed ? (
					<Message
						severity="warn"
						text="Ce transcript n'a pas été déclaré relu. L'export portera la version brute de la transcription automatique."
					/>
				) : null}
				<p css={scopeWarningStyle}>
					Un transcript exporté sort du périmètre de l'application : ni cloisonnement, ni purge, ni
					journal ne s'appliquent plus au fichier.
				</p>
			</div>
		</Dialog>
	);
};

export default TranscriptExportDialog;
